import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { api } from '@/lib/api'
import { BarChart, type BarDatum } from '@/components/BarChart'
import { ErrorState, Skeleton } from '@/components/States'
import { CsvButton } from '@/components/CsvButton'
import { RefreshButton } from '@/components/RefreshButton'
import { downloadCsv } from '@/lib/csv'

// A16.31 — Ball (points) iqtisodiyoti. Modul-lokal (adapters.ts ga tegmaslik + boshqa
// sessiyalar tahrirlayotgan AnalyticsMetrics/Screens'dan izolyatsiya uchun alohida fayl).
// Backend: GET /admin/analytics/points (`handlers/admin.go` AnalyticsPoints). PII-siz agregat.
interface PointsEconomyData {
  circulating: number
  lifetime_earned: number
  total_spent: number
  holders: number
  suspended: number
  purchases: number
  earn_by_reason: { reason: string; points: number }[]
  spend_by_item: { title: string; count: number }[]
}
function fetchPoints(signal?: AbortSignal): Promise<PointsEconomyData> {
  return api.get<PointsEconomyData>('/admin/analytics/points', signal ? { signal } : undefined)
}

function KPI({ label, value, loading, accent }: { label: string; value?: number; loading: boolean; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-8 w-16" />
      ) : (
        <div className={`mt-0.5 font-mono text-2xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>
          {value?.toLocaleString() ?? '—'}
        </div>
      )}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="max-w-3xl rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}

/** A16.31 — Ball iqtisodiyoti (muomala/ishlangan/sarflangan + manba/sarf taqsimoti). */
export function PointsEconomy() {
  const t = useT()
  const [data, setData] = useState<PointsEconomyData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchPoints(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const earnBars = useMemo<BarDatum[]>(
    () => (data?.earn_by_reason ?? []).map((e) => ({ label: e.reason, value: e.points })),
    [data],
  )
  const spendBars = useMemo<BarDatum[]>(
    () => (data?.spend_by_item ?? []).map((s) => ({ label: s.title, value: s.count })),
    [data],
  )
  const handleExport = () => {
    if (!data) return
    downloadCsv('ball-economy', [
      [t.analytics.pointsCirculating, t.analytics.pointsEarned, t.analytics.pointsSpent, t.analytics.pointsHolders, t.analytics.pointsPurchases, t.analytics.pointsSuspended],
      [data.circulating, data.lifetime_earned, data.total_spent, data.holders, data.purchases, data.suspended],
      [],
      [t.analytics.pointsEarnByReason, t.analytics.pointsEarned],
      ...data.earn_by_reason.map((e) => [e.reason, e.points]),
      [],
      [t.analytics.pointsSpendByItem, t.analytics.pointsPurchases],
      ...data.spend_by_item.map((s) => [s.title, s.count]),
    ])
  }
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader
        title={t.analytics.pointsEconomy}
        screenId="A16.31"
        description={t.analytics.pointsNote}
        actions={
          <div className="flex items-center gap-2">
            <RefreshButton onClick={() => setReload((n) => n + 1)} />
            {data && <CsvButton onClick={handleExport} />}
          </div>
        }
      />
      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-3">
          <KPI label={t.analytics.pointsCirculating} value={data?.circulating} loading={loading} accent />
          <KPI label={t.analytics.pointsEarned} value={data?.lifetime_earned} loading={loading} />
          <KPI label={t.analytics.pointsSpent} value={data?.total_spent} loading={loading} />
          <KPI label={t.analytics.pointsHolders} value={data?.holders} loading={loading} />
          <KPI label={t.analytics.pointsPurchases} value={data?.purchases} loading={loading} />
          <KPI label={t.analytics.pointsSuspended} value={data?.suspended} loading={loading} />
        </div>
        <Panel title={t.analytics.pointsEarnByReason}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={earnBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.pointsSpendByItem}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={spendBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}
